import { memoryCache } from "@/lib/memorycache"

export class SortedMap<T extends Record<string, unknown>> {
  private dataMap: Map<string, T>
  private sortMap: Map<number, string[]>

  private readonly timestampKey: string
  private readonly idKey: string
  private readonly cacheKey: string

  constructor(idKey: string, timestampKey: string, cacheKey?: string) {
    this.timestampKey = timestampKey
    this.idKey = idKey

    if (cacheKey) {
      this.cacheKey = cacheKey
    } else {
      this.cacheKey = crypto.randomUUID()
    }

    this.dataMap = new Map()
    this.sortMap = new Map()
  }

  public set(key: string, value: T): void {
    this.dataMap.set(key, value)

    const dataTimestamp: number =
      typeof value[this.timestampKey] === "number"
        ? (value[this.timestampKey] as number)
        : parseInt(`${value[this.timestampKey]}`, 10)
    const dataID =
      typeof value[this.idKey] === "string"
        ? (value[this.idKey] as string)
        : `${value[this.idKey]}`

    const timestampSortData = this.sortMap.get(dataTimestamp)

    if (!timestampSortData) {
      this.sortMap.set(dataTimestamp, [key])
    } else if (!timestampSortData.includes(dataID.toString())) {
      this.sortMap.set(dataTimestamp, [...timestampSortData, dataID.toString()])
    } else {
      // There is nothing to do since the data is already in the sortMap
    }

    // Invalidate the cache
    memoryCache.invalidate(this.cacheKey)
  }

  public get(key: string): T | undefined {
    return this.dataMap.get(key)
  }

  public remove(key: string) {
    if (!this.dataMap.has(key)) return

    const itemValue = this.dataMap.get(key)! // We know that the key exists, we checked it above
    const dataTimestamp: number =
      typeof itemValue[this.timestampKey] === "number"
        ? (itemValue[this.timestampKey] as number)
        : parseInt(`${itemValue[this.timestampKey]}`, 10)
    const dataID =
      typeof itemValue[this.idKey] === "string"
        ? (itemValue[this.idKey] as string)
        : `${itemValue[this.idKey]}`

    // Remove it from the sortMap
    if (this.sortMap.has(dataTimestamp)) {
      const timestampSortData = this.sortMap
        .get(dataTimestamp)! // We know that the key exists, we checked it above
        .filter((id) => id !== dataID.toString())
      if (timestampSortData.length === 0) {
        this.sortMap.delete(dataTimestamp)
      } else {
        this.sortMap.set(dataTimestamp, timestampSortData)
      }
    }

    // Remove it from the dataMap
    this.dataMap.delete(key)

    // Invalidate the cache
    memoryCache.invalidate(this.cacheKey)
  }

  public getSortedItems(): Array<[string, T]> {
    const cache = memoryCache.get<Array<[string, T]>>(this.cacheKey)
    if (cache) {
      return cache
    }

    const sortedItems: Array<[string, T]> = []

    const sortedKeys = Array.from(this.sortMap.keys()).sort((a, b) => a - b)

    for (const key of sortedKeys) {
      const ids = this.sortMap.get(key)!
      for (const id of ids) {
        const item = this.dataMap.get(id)
        if (item) {
          sortedItems.push([id, item])
        }
      }
    }

    memoryCache.set(this.cacheKey, sortedItems)

    return sortedItems
  }
}
